import React from 'react';
import { Box, Stack, Typography, Tooltip } from '@material-ui/core';

const SubTaskMeterChart = ({ subTasks, subtaskCount, completedCount, theme, width = '100%' }) => {
  const isLight = theme.palette.mode === 'light';
  const hasSubTasks = Array.isArray(subTasks) && subTasks.length > 0;
  const totalSteps = hasSubTasks ? subTasks.length : (subtaskCount || 0);
  const completedSteps = hasSubTasks 
    ? subTasks.filter(st => st.status === 3).length 
    : (completedCount || 0);

  if (totalSteps === 0) return null;

  // Determine status color configurations
  const getStatusDetails = (status) => {
    switch (status) {
      case 2: // In Progress
        return {
          color: '#3b82f6', // Electric vibrant blue
          glow: 'rgba(59, 130, 246, 0.4)',
          name: 'In Progress',
          isAnimating: true,
        };
      case 3: // Completed
        return {
          color: '#10b981', // Clean emerald green
          glow: 'rgba(16, 185, 129, 0.35)',
          name: 'Completed',
          isAnimating: false,
        };
      case 4: // Hold
        return {
          color: '#f59e0b', // Warm amber orange
          glow: 'rgba(245, 158, 11, 0.35)',
          name: 'On Hold',
          isAnimating: false,
        };
      case 9: // Rejected
        return {
          color: '#ef4444', // Crimson red
          glow: 'rgba(239, 68, 68, 0.35)',
          name: 'Rejected',
          isAnimating: false,
        };
      default: // New / Pending
        return {
          color: isLight ? '#e2e8f0' : '#475569', // Soft slate border / background
          glow: 'rgba(0, 0, 0, 0)',
          name: 'New',
          isAnimating: false,
        };
    }
  };

  const steps = [];
  if (hasSubTasks) {
    subTasks.forEach((sub, idx) => {
      const details = getStatusDetails(sub.status);
      steps.push({
        id: sub.ID || sub.id || idx,
        name: sub.name || `Step ${idx + 1}`,
        color: details.color,
        glow: details.glow,
        status: details.name,
        isAnimating: details.isAnimating,
      });
    });
  } else {
    for (let i = 0; i < totalSteps; i++) {
      const isCompleted = i < completedSteps;
      steps.push({
        id: i,
        name: `Step ${i + 1}`,
        color: isCompleted ? '#10b981' : (isLight ? '#e2e8f0' : '#475569'),
        glow: isCompleted ? 'rgba(16, 185, 129, 0.35)' : 'rgba(0,0,0,0)',
        status: isCompleted ? 'Completed' : 'Pending',
        isAnimating: false,
      });
    }
  }

  const percentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // Determine overall badge status color for dot indicator
  let overallDotColor = '#94a3b8'; // Slate grey
  if (percentage === 100) {
    overallDotColor = '#10b981'; // Completed (Green)
  } else if (steps.some(s => s.status === 'In Progress')) {
    overallDotColor = '#3b82f6'; // In Progress (Blue)
  } else if (steps.some(s => s.status === 'On Hold')) {
    overallDotColor = '#f59e0b'; // Hold (Amber)
  } else if (steps.some(s => s.status === 'Rejected')) {
    overallDotColor = '#ef4444'; // Rejected (Red)
  }

  return (
    <Box 
      sx={{ 
        mt: 1.5, 
        mb: 1.5, 
        width: width,
        background: isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)',
        border: '1px solid',
        borderColor: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
        borderRadius: '8px',
        p: '10px 12px',
        boxSizing: 'border-box'
      }}
    >
      <style>{`
        @keyframes pulse-glow {
          0% { box-shadow: 0 0 2px rgba(59, 130, 246, 0.2); opacity: 0.85; }
          50% { box-shadow: 0 0 8px rgba(59, 130, 246, 0.7); opacity: 1; }
          100% { box-shadow: 0 0 2px rgba(59, 130, 246, 0.2); opacity: 0.85; }
        }
        .step-pulse-animation {
          animation: pulse-glow 2s infinite ease-in-out;
        }
      `}</style>

      {/* Progress segmented pills */}
      <Stack
        direction="row"
        spacing={0.75}
        sx={{
          width: '100%',
          height: 8,
          alignItems: 'center',
          mb: 1
        }}
      >
        {steps.map((step) => (
          <Tooltip
            key={step.id}
            title={
              <Box sx={{ p: 0.5, minWidth: 80 }}>
                <Typography variant="body2" sx={{ fontWeight: '700', color: '#ffffff', fontSize: '0.75rem' }}>
                  {step.name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: step.color }} />
                  <Typography variant="caption" sx={{ color: '#cbd5e1', fontSize: '0.65rem', fontWeight: '500' }}>
                    {step.status}
                  </Typography>
                </Stack>
              </Box>
            }
            arrow
            placement="top"
          >
            <Box
              className={step.isAnimating ? 'step-pulse-animation' : ''}
              sx={{
                flex: 1,
                backgroundColor: step.color,
                height: '100%',
                borderRadius: '4px',
                boxShadow: step.glow !== 'rgba(0, 0, 0, 0)' ? `0 1px 4px ${step.glow}` : 'none',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  filter: 'brightness(1.15)',
                  transform: 'translateY(-1.5px) scaleY(1.25)',
                  boxShadow: `0 3px 8px ${step.glow !== 'rgba(0, 0, 0, 0)' ? step.glow : 'rgba(0,0,0,0.15)'}`,
                },
              }}
            />
          </Tooltip>
        ))}
      </Stack>

      {/* Metadata / Details Footer */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 0.25 }}>
        {/* Step Indicator status */}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box 
            sx={{ 
              width: 7, 
              height: 7, 
              borderRadius: '50%', 
              backgroundColor: overallDotColor,
              boxShadow: `0 0 5px ${overallDotColor}`
            }} 
          />
          <Typography
            variant="caption"
            sx={{ 
              color: isLight ? 'text.secondary' : '#94a3b8', 
              fontWeight: '600', 
              fontSize: '0.68rem',
              letterSpacing: '0.01em'
            }}
          >
            Workflow: {completedSteps}/{totalSteps} steps
          </Typography>
        </Stack>

        {/* Completion percentage pill */}
        <Box
          sx={{
            background: percentage === 100 
              ? 'rgba(16, 185, 129, 0.12)' 
              : isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.05)',
            border: '1px solid',
            borderColor: percentage === 100 
              ? 'rgba(16, 185, 129, 0.2)' 
              : isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)',
            borderRadius: '12px',
            px: 1.25,
            py: 0.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: percentage === 100 
                ? '#10b981' 
                : isLight ? 'text.primary' : '#e2e8f0',
              fontWeight: '700',
              fontSize: '0.68rem',
              letterSpacing: '0.02em'
            }}
          >
            {percentage}% Done
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default SubTaskMeterChart;
